/**
 * Error State Visualization & Recovery System
 * Comprehensive error feedback with shake animations, recovery patterns, and user guidance
 */

import {
  ErrorType,
  ErrorVisualizationConfig,
  RecoveryAction,
  VisualFeedbackContext,
  VisualFeedbackState,
} from "./types";
import { HardwareAcceleratedAnimationFramework } from "./HardwareAcceleratedAnimationFramework";
import { saveBeforeNavigate } from "@/utils/safeNavigation";

/**
 * Main Error State Visualization System
 * Handles visual feedback for all error scenarios with clear recovery guidance
 */
export class ErrorStateVisualizationSystem {
  private animationFramework: HardwareAcceleratedAnimationFramework;
  private errorConfigurations: Map<ErrorType, ErrorVisualizationConfig> =
    new Map();
  private recoveryActions: Map<ErrorType, RecoveryAction[]> = new Map();
  private activeErrorElements: Map<string, HTMLElement> = new Map();
  private errorTooltipManager: ErrorTooltipManager;
  private recoveryGuidanceSystem: RecoveryGuidanceSystem;
  private errorLogger: ErrorLogger;

  constructor(animationFramework: HardwareAcceleratedAnimationFramework) {
    this.animationFramework = animationFramework;
    this.errorTooltipManager = new ErrorTooltipManager();
    this.recoveryGuidanceSystem = new RecoveryGuidanceSystem();
    this.errorLogger = new ErrorLogger();

    this.initializeErrorSystem();
  }

  /**
   * Initialize the error visualization system
   */
  private initializeErrorSystem(): void {
    this.setupDefaultErrorConfigurations();
    this.setupDefaultRecoveryActions();
    this.initializeErrorStyles();
  }

  /**
   * Setup default error configurations for all error types
   */
  private setupDefaultErrorConfigurations(): void {
    // Invalid Drop Configuration
    this.errorConfigurations.set(ErrorType.INVALID_DROP, {
      errorType: ErrorType.INVALID_DROP,
      shakeAnimation: {
        enabled: true,
        intensity: "moderate",
        duration: 400,
        frequency: 3,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--destructive))",
        overlayOpacity: 0.1,
        borderHighlight: true,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText:
          "This component cannot be placed here. Try a different location.",
        suggestedActions: ["Show valid drop zones", "Cancel operation"],
        autoSuggestAlternatives: true,
      },
    });

    // Constraint Violation Configuration
    this.errorConfigurations.set(ErrorType.CONSTRAINT_VIOLATION, {
      errorType: ErrorType.CONSTRAINT_VIOLATION,
      shakeAnimation: {
        enabled: true,
        intensity: "subtle",
        duration: 300,
        frequency: 2,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--warning))",
        overlayOpacity: 0.15,
        borderHighlight: true,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText: "This placement violates layout constraints.",
        suggestedActions: [
          "Auto-correct placement",
          "Force placement",
          "Cancel",
        ],
        autoSuggestAlternatives: true,
      },
    });

    // Network Error Configuration
    this.errorConfigurations.set(ErrorType.NETWORK_ERROR, {
      errorType: ErrorType.NETWORK_ERROR,
      shakeAnimation: {
        enabled: false,
        intensity: "subtle",
        duration: 0,
        frequency: 0,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--warning))",
        overlayOpacity: 0.05,
        borderHighlight: false,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText: "Connection lost. Changes are being saved locally.",
        suggestedActions: ["Retry now", "Work offline", "View queued changes"],
        autoSuggestAlternatives: false,
      },
    });

    // Permission Denied Configuration
    this.errorConfigurations.set(ErrorType.PERMISSION_DENIED, {
      errorType: ErrorType.PERMISSION_DENIED,
      shakeAnimation: {
        enabled: true,
        intensity: "strong",
        duration: 500,
        frequency: 2,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--destructive))",
        overlayOpacity: 0.2,
        borderHighlight: true,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText: "You don't have permission to perform this action.",
        suggestedActions: [
          "Request permission",
          "Switch account",
          "Contact administrator",
        ],
        autoSuggestAlternatives: false,
      },
    });

    // Collision Detected Configuration
    this.errorConfigurations.set(ErrorType.COLLISION_DETECTED, {
      errorType: ErrorType.COLLISION_DETECTED,
      shakeAnimation: {
        enabled: true,
        intensity: "moderate",
        duration: 350,
        frequency: 4,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--warning))",
        overlayOpacity: 0.12,
        borderHighlight: true,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText: "Component overlaps with existing elements.",
        suggestedActions: [
          "Auto-arrange",
          "Manual adjustment",
          "Replace existing",
        ],
        autoSuggestAlternatives: true,
      },
    });

    // System Error Configuration
    this.errorConfigurations.set(ErrorType.SYSTEM_ERROR, {
      errorType: ErrorType.SYSTEM_ERROR,
      shakeAnimation: {
        enabled: false,
        intensity: "subtle",
        duration: 0,
        frequency: 0,
      },
      colorIndicators: {
        enabled: true,
        errorColor: "hsl(var(--destructive))",
        overlayOpacity: 0.25,
        borderHighlight: true,
      },
      recoveryGuidance: {
        enabled: true,
        tooltip: true,
        tooltipText: "A system error occurred. Please try again.",
        suggestedActions: ["Retry operation", "Refresh page", "Report issue"],
        autoSuggestAlternatives: false,
      },
    });
  }

  /**
   * Setup default recovery actions for each error type
   */
  private setupDefaultRecoveryActions(): void {
    // Invalid Drop Recovery Actions
    this.recoveryActions.set(ErrorType.INVALID_DROP, [
      {
        id: "show-valid-zones",
        label: "Show Valid Locations",
        action: () => this.highlightValidAreas(),
        icon: "🎯",
        priority: "primary",
      },
      {
        id: "cancel-drop",
        label: "Cancel",
        action: () => this.cancelDropOperation(),
        icon: "❌",
        priority: "secondary",
      },
    ]);

    // Constraint Violation Recovery Actions
    this.recoveryActions.set(ErrorType.CONSTRAINT_VIOLATION, [
      {
        id: "auto-correct",
        label: "Auto-Correct",
        action: () => this.autoCorrectPlacement(),
        icon: "✨",
        priority: "primary",
      },
      {
        id: "force-placement",
        label: "Force Placement",
        action: () => this.forcePlacement(),
        icon: "💪",
        priority: "secondary",
        destructive: true,
      },
      {
        id: "cancel-operation",
        label: "Cancel",
        action: () => this.cancelOperation(),
        icon: "❌",
        priority: "tertiary",
      },
    ]);

    // Network Error Recovery Actions
    this.recoveryActions.set(ErrorType.NETWORK_ERROR, [
      {
        id: "retry-network",
        label: "Retry Now",
        action: () => this.retryNetworkOperation(),
        icon: "🔄",
        priority: "primary",
      },
      {
        id: "work-offline",
        label: "Work Offline",
        action: () => this.enableOfflineMode(),
        icon: "📡",
        priority: "secondary",
      },
      {
        id: "view-queued",
        label: "View Queued Changes",
        action: () => this.showQueuedChanges(),
        icon: "📋",
        priority: "tertiary",
      },
    ]);

    // Permission Denied Recovery Actions
    this.recoveryActions.set(ErrorType.PERMISSION_DENIED, [
      {
        id: "request-permission",
        label: "Request Permission",
        action: () => this.requestPermission(),
        icon: "🔑",
        priority: "primary",
      },
      {
        id: "switch-account",
        label: "Switch Account",
        action: () => this.switchAccount(),
        icon: "👤",
        priority: "secondary",
      },
      {
        id: "contact-admin",
        label: "Contact Administrator",
        action: () => this.contactAdministrator(),
        icon: "📧",
        priority: "tertiary",
      },
    ]);

    // Collision Detected Recovery Actions
    this.recoveryActions.set(ErrorType.COLLISION_DETECTED, [
      {
        id: "auto-arrange",
        label: "Auto-Arrange",
        action: () => this.autoArrangeComponents(),
        icon: "🔧",
        priority: "primary",
      },
      {
        id: "manual-adjust",
        label: "Manual Adjustment",
        action: () => this.enableManualAdjustment(),
        icon: "✋",
        priority: "secondary",
      },
      {
        id: "replace-existing",
        label: "Replace Existing",
        action: () => this.replaceExistingComponent(),
        icon: "🔄",
        priority: "tertiary",
        destructive: true,
      },
    ]);

    // System Error Recovery Actions
    this.recoveryActions.set(ErrorType.SYSTEM_ERROR, [
      {
        id: "retry-operation",
        label: "Retry Operation",
        action: () => this.retryLastOperation(),
        icon: "🔄",
        priority: "primary",
      },
      {
        id: "refresh-page",
        label: "Refresh Page",
        action: () => this.refreshPage(),
        icon: "🔄",
        priority: "secondary",
      },
      {
        id: "report-issue",
        label: "Report Issue",
        action: () => this.reportIssue(),
        icon: "🐛",
        priority: "tertiary",
      },
    ]);
  }

  /**
   * Initialize error-specific CSS styles
   */
  private initializeErrorStyles(): void {
    const styleSheet = this.createErrorStyleSheet();
    document.head.appendChild(styleSheet);
  }

  /**
   * Create CSS stylesheet for error visualizations
   */
  private createErrorStyleSheet(): HTMLStyleElement {
    const style = document.createElement("style");
    style.id = "terra-error-visualization-styles";

    style.textContent = `
      /* Error State Base Styles */
      .terra-error-state {
        position: relative;
        z-index: 1000;
      }

      /* Shake Animations */
      .terra-shake-subtle {
        animation: shake-subtle 0.3s ease-in-out;
      }

      .terra-shake-moderate {
        animation: shake-moderate 0.4s ease-in-out;
      }

      .terra-shake-strong {
        animation: shake-strong 0.5s ease-in-out;
      }

      @keyframes shake-subtle {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
        20%, 40%, 60%, 80% { transform: translateX(2px); }
      }

      @keyframes shake-moderate {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
        20%, 40%, 60%, 80% { transform: translateX(4px); }
      }

      @keyframes shake-strong {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
        20%, 40%, 60%, 80% { transform: translateX(6px); }
      }

      /* Error Overlays */
      .terra-error-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: -1;
      }

      .terra-error-overlay-invalid {
        background: hsl(var(--destructive) / 0.1);
        border: 2px solid hsl(var(--destructive) / 0.5);
      }

      .terra-error-overlay-warning {
        background: hsl(var(--warning) / 0.15);
        border: 2px solid hsl(var(--warning) / 0.6);
      }

      .terra-error-overlay-network {
        background: hsl(var(--warning) / 0.05);
        border: 1px solid hsl(var(--warning) / 0.3);
      }

      /* Error Tooltips */
      .terra-error-tooltip {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 8px;
        padding: 12px 16px;
        background: hsl(var(--destructive));
        color: hsl(var(--destructive-foreground));
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 500;
        max-width: 280px;
        text-align: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 1001;
        opacity: 0;
        animation: tooltip-appear 0.2s ease-out forwards;
      }

      .terra-error-tooltip::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: hsl(var(--destructive));
      }

      .terra-error-tooltip-warning {
        background: hsl(var(--warning));
        color: hsl(var(--warning-foreground));
      }

      .terra-error-tooltip-warning::after {
        border-top-color: hsl(var(--warning));
      }

      @keyframes tooltip-appear {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }

      /* Recovery Action Panel */
      .terra-recovery-panel {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 8px;
        background: hsl(var(--background));
        border: 1px solid hsl(var(--border));
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 1001;
        opacity: 0;
        animation: panel-slide-up 0.3s ease-out forwards;
      }

      @keyframes panel-slide-up {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .terra-recovery-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 12px;
      }

      .terra-recovery-action {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border: 1px solid hsl(var(--border));
        border-radius: 6px;
        background: hsl(var(--background));
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        transition: all 0.2s ease;
      }

      .terra-recovery-action:hover {
        background: hsl(var(--muted));
        border-color: hsl(var(--primary));
        transform: translateY(-1px);
      }

      .terra-recovery-action-primary {
        background: hsl(var(--primary));
        color: hsl(var(--primary-foreground));
        border-color: hsl(var(--primary));
      }

      .terra-recovery-action-primary:hover {
        background: hsl(var(--primary) / 0.9);
      }

      .terra-recovery-action-destructive {
        border-color: hsl(var(--destructive));
        color: hsl(var(--destructive));
      }

      .terra-recovery-action-destructive:hover {
        background: hsl(var(--destructive) / 0.1);
      }

      /* Valid Drop Zone Highlighting */
      .terra-valid-area {
        border: 2px dashed hsl(var(--success));
        background: hsl(var(--success) / 0.1);
        animation: valid-zone-pulse 2s ease-in-out infinite;
      }

      @keyframes valid-zone-pulse {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
      }

      /* Alternative Suggestion Highlighting */
      .terra-suggested-alternative {
        border: 2px solid hsl(var(--primary));
        background: hsl(var(--primary) / 0.05);
        animation: suggestion-glow 1.5s ease-in-out 3;
      }

      @keyframes suggestion-glow {
        0%, 100% { box-shadow: 0 0 0 rgba(var(--primary-rgb), 0.3); }
        50% { box-shadow: 0 0 20px rgba(var(--primary-rgb), 0.6); }
      }

      /* Progress Indicators for Recovery Actions */
      .terra-recovery-progress {
        position: absolute;
        bottom: 2px;
        left: 0;
        height: 2px;
        background: hsl(var(--primary));
        border-radius: 1px;
        transform-origin: left;
        transform: scaleX(0);
        animation: progress-fill 1s ease-out forwards;
      }

      @keyframes progress-fill {
        to { transform: scaleX(1); }
      }

      /* Reduced Motion Support */
      @media (prefers-reduced-motion: reduce) {
        .terra-shake-subtle,
        .terra-shake-moderate,
        .terra-shake-strong,
        .terra-error-tooltip,
        .terra-recovery-panel,
        .terra-valid-area,
        .terra-suggested-alternative {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
        }
      }

      /* High Contrast Mode Support */
      @media (prefers-contrast: high) {
        .terra-error-overlay {
          border-width: 3px;
        }

        .terra-error-tooltip {
          border: 2px solid;
        }

        .terra-recovery-action {
          border-width: 2px;
        }
      }
    `;

    return style;
  }

  /**
   * Main entry point for error visualization
   */
  public async visualizeError(
    errorType: ErrorType,
    context: VisualFeedbackContext,
    element?: HTMLElement,
    errorMessage?: string,
    additionalData?: any,
  ): Promise<void> {
    if (!element) {
      console.warn("Cannot visualize error without element");
      return;
    }

    return this.handleError(
      errorType,
      context,
      element,
      errorMessage,
      additionalData,
    );
  }

  /**
   * Handle error state visualization
   */
  public async handleError(
    errorType: ErrorType,
    context: VisualFeedbackContext,
    element: HTMLElement,
    errorMessage?: string,
    additionalData?: any,
  ): Promise<void> {
    // Log error for debugging and analytics
    this.errorLogger.logError(errorType, context, errorMessage, additionalData);

    const config = this.errorConfigurations.get(errorType);
    if (!config) {
      console.warn(`No configuration found for error type: ${errorType}`);
      return;
    }

    // Apply error state
    element.classList.add("terra-error-state");
    this.activeErrorElements.set(element.id || `error-${Date.now()}`, element);

    // Apply visual feedback based on configuration
    await this.applyErrorVisualization(element, config, context);

    // Show recovery guidance if enabled
    if (config.recoveryGuidance.enabled) {
      await this.showRecoveryGuidance(element, errorType, config, context);
    }

    // Auto-suggest alternatives if enabled
    if (config.recoveryGuidance.autoSuggestAlternatives) {
      this.suggestAlternatives(element, errorType, context);
    }
  }

  /**
   * Apply error visualization based on configuration
   */
  private async applyErrorVisualization(
    element: HTMLElement,
    config: ErrorVisualizationConfig,
    context: VisualFeedbackContext,
  ): Promise<void> {
    // Apply shake animation if enabled
    if (config.shakeAnimation.enabled) {
      await this.applyShakeAnimation(element, config.shakeAnimation);
    }

    // Apply color indicators if enabled
    if (config.colorIndicators.enabled) {
      this.applyColorIndicators(element, config.colorIndicators);
    }
  }

  /**
   * Apply shake animation
   */
  private async applyShakeAnimation(
    element: HTMLElement,
    config: any,
  ): Promise<void> {
    const shakeClass = `terra-shake-${config.intensity}`;
    element.classList.add(shakeClass);

    // Remove shake class after animation
    setTimeout(() => {
      element.classList.remove(shakeClass);
    }, config.duration);
  }

  /**
   * Apply color indicators
   */
  private applyColorIndicators(element: HTMLElement, config: any): void {
    // Create error overlay
    const overlay = document.createElement("div");
    overlay.className = "terra-error-overlay";

    // Apply appropriate overlay style based on error color
    if (config.errorColor.includes("destructive")) {
      overlay.classList.add("terra-error-overlay-invalid");
    } else if (config.errorColor.includes("warning")) {
      overlay.classList.add("terra-error-overlay-warning");
    } else {
      overlay.classList.add("terra-error-overlay-network");
    }

    overlay.style.opacity = config.overlayOpacity.toString();
    element.appendChild(overlay);

    // Apply border highlight if enabled
    if (config.borderHighlight) {
      element.style.borderColor = config.errorColor;
      element.style.borderWidth = "2px";
      element.style.borderStyle = "solid";
    }
  }

  /**
   * Show recovery guidance
   */
  private async showRecoveryGuidance(
    element: HTMLElement,
    errorType: ErrorType,
    config: ErrorVisualizationConfig,
    context: VisualFeedbackContext,
  ): Promise<void> {
    if (config.recoveryGuidance.tooltip) {
      this.errorTooltipManager.showTooltip(
        element,
        config.recoveryGuidance.tooltipText,
        errorType,
      );
    }

    // Show recovery actions panel
    const recoveryActions = this.recoveryActions.get(errorType);
    if (recoveryActions && recoveryActions.length > 0) {
      this.recoveryGuidanceSystem.showRecoveryPanel(
        element,
        recoveryActions,
        errorType,
      );
    }
  }

  /**
   * Suggest alternatives for error resolution
   */
  private suggestAlternatives(
    element: HTMLElement,
    errorType: ErrorType,
    context: VisualFeedbackContext,
  ): void {
    switch (errorType) {
      case ErrorType.INVALID_DROP:
        this.suggestValidDropZones(context);
        break;
      case ErrorType.CONSTRAINT_VIOLATION:
        this.suggestConstraintFriendlyPositions(element, context);
        break;
      case ErrorType.COLLISION_DETECTED:
        this.suggestNonCollidingPositions(element, context);
        break;
    }
  }

  /**
   * Clear error state from element
   */
  public clearError(element: HTMLElement): void {
    // Remove error classes
    element.classList.remove(
      "terra-error-state",
      "terra-shake-subtle",
      "terra-shake-moderate",
      "terra-shake-strong",
    );

    // Remove error overlays
    element
      .querySelectorAll(".terra-error-overlay")
      .forEach((overlay) => overlay.remove());

    // Clear border styling
    element.style.borderColor = "";
    element.style.borderWidth = "";
    element.style.borderStyle = "";

    // Hide tooltips and recovery panels
    this.errorTooltipManager.hideTooltip(element);
    this.recoveryGuidanceSystem.hideRecoveryPanel(element);

    // Clear alternative suggestions
    this.clearAlternativeSuggestions();

    // Remove from active elements
    const elementId =
      element.id ||
      Array.from(this.activeErrorElements.entries()).find(
        ([, el]) => el === element,
      )?.[0];
    if (elementId) {
      this.activeErrorElements.delete(elementId);
    }
  }

  /**
   * Clear all error states
   */
  public clearAllErrors(): void {
    this.activeErrorElements.forEach((element) => {
      this.clearError(element);
    });
    this.clearAlternativeSuggestions();
  }

  // Recovery action implementations
  private highlightValidAreas(): void {
    const validAreas = document.querySelectorAll('[data-canvas="true"]');
    validAreas.forEach((area) => {
      (area as HTMLElement).classList.add("terra-valid-area");
    });

    // Clear highlights after 3 seconds
    setTimeout(() => {
      validAreas.forEach((zone: Element) => {
        (zone as HTMLElement).classList.remove("terra-valid-area");
      });
    }, 3000);
  }

  private cancelDropOperation(): void {
    // Emit event to cancel current drag operation
    window.dispatchEvent(new CustomEvent("terra:cancel-drop"));
  }

  private autoCorrectPlacement(): void {
    window.dispatchEvent(new CustomEvent("terra:auto-correct-placement"));
  }

  private forcePlacement(): void {
    window.dispatchEvent(new CustomEvent("terra:force-placement"));
  }

  private cancelOperation(): void {
    window.dispatchEvent(new CustomEvent("terra:cancel-operation"));
  }

  private retryNetworkOperation(): void {
    window.dispatchEvent(new CustomEvent("terra:retry-network"));
  }

  private enableOfflineMode(): void {
    window.dispatchEvent(new CustomEvent("terra:enable-offline"));
  }

  private showQueuedChanges(): void {
    window.dispatchEvent(new CustomEvent("terra:show-queued-changes"));
  }

  private requestPermission(): void {
    window.dispatchEvent(new CustomEvent("terra:request-permission"));
  }

  private switchAccount(): void {
    window.dispatchEvent(new CustomEvent("terra:switch-account"));
  }

  private contactAdministrator(): void {
    window.dispatchEvent(new CustomEvent("terra:contact-admin"));
  }

  private autoArrangeComponents(): void {
    window.dispatchEvent(new CustomEvent("terra:auto-arrange"));
  }

  private enableManualAdjustment(): void {
    window.dispatchEvent(new CustomEvent("terra:manual-adjustment"));
  }

  private replaceExistingComponent(): void {
    window.dispatchEvent(new CustomEvent("terra:replace-existing"));
  }

  private retryLastOperation(): void {
    window.dispatchEvent(new CustomEvent("terra:retry-operation"));
  }

  private refreshPage(): void {
    saveBeforeNavigate().finally(() => window.location.reload());
  }

  private reportIssue(): void {
    window.dispatchEvent(new CustomEvent("terra:report-issue"));
  }

  // Suggestion methods
  private suggestValidDropZones(context: VisualFeedbackContext): void {
    // Highlight valid drop zones based on component type
    const validZones = document.querySelectorAll(
      `[data-accepts*="${context.componentType}"]`,
    );
    validZones.forEach((zone) => {
      (zone as HTMLElement).classList.add("terra-suggested-alternative");
    });
  }

  private suggestConstraintFriendlyPositions(
    element: HTMLElement,
    context: VisualFeedbackContext,
  ): void {
    // Find positions that don't violate constraints
    const alternatives = this.findConstraintFriendlyPositions(context);
    this.highlightPositionAlternatives(alternatives);
  }

  private suggestNonCollidingPositions(
    element: HTMLElement,
    context: VisualFeedbackContext,
  ): void {
    // Find positions that don't collide with existing components
    const alternatives = this.findNonCollidingPositions(context);
    this.highlightPositionAlternatives(alternatives);
  }

  private findConstraintFriendlyPositions(
    context: VisualFeedbackContext,
  ): Position[] {
    // Implementation would analyze layout constraints and return valid positions
    return [];
  }

  private findNonCollidingPositions(
    context: VisualFeedbackContext,
  ): Position[] {
    // Implementation would analyze component positions and return non-colliding positions
    return [];
  }

  private highlightPositionAlternatives(positions: Position[]): void {
    positions.forEach((position, index) => {
      const marker = document.createElement("div");
      marker.className = "terra-position-alternative";
      marker.style.cssText = `
        position: absolute;
        left: ${position.x}px;
        top: ${position.y}px;
        width: 20px;
        height: 20px;
        background: hsl(var(--success));
        border-radius: 50%;
        animation: alternative-pulse 1s ease-in-out infinite;
        animation-delay: ${index * 0.1}s;
        z-index: 1000;
      `;
      document.body.appendChild(marker);
    });
  }

  private clearAlternativeSuggestions(): void {
    document
      .querySelectorAll(
        ".terra-valid-area, .terra-suggested-alternative, .terra-position-alternative",
      )
      .forEach((el) => el.remove());
  }

  /**
   * Get error statistics
   */
  public getErrorStatistics() {
    return this.errorLogger.getStatistics();
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    this.clearAllErrors();
    this.errorTooltipManager.dispose();
    this.recoveryGuidanceSystem.dispose();

    // Remove stylesheet
    const styleSheet = document.getElementById(
      "terra-error-visualization-styles",
    );
    if (styleSheet) {
      styleSheet.remove();
    }
  }
}

/**
 * Supporting classes
 */

class ErrorTooltipManager {
  private activeTooltips: Map<HTMLElement, HTMLElement> = new Map();

  showTooltip(element: HTMLElement, text: string, errorType: ErrorType): void {
    const tooltip = document.createElement("div");
    tooltip.className = "terra-error-tooltip";

    if (
      errorType === ErrorType.NETWORK_ERROR ||
      errorType === ErrorType.CONSTRAINT_VIOLATION
    ) {
      tooltip.classList.add("terra-error-tooltip-warning");
    }

    tooltip.textContent = text;
    element.appendChild(tooltip);

    this.activeTooltips.set(element, tooltip);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideTooltip(element);
    }, 5000);
  }

  hideTooltip(element: HTMLElement): void {
    const tooltip = this.activeTooltips.get(element);
    if (tooltip) {
      tooltip.remove();
      this.activeTooltips.delete(element);
    }
  }

  dispose(): void {
    this.activeTooltips.forEach((tooltip, element) => {
      tooltip.remove();
    });
    this.activeTooltips.clear();
  }
}

class RecoveryGuidanceSystem {
  private activePanels: Map<HTMLElement, HTMLElement> = new Map();

  showRecoveryPanel(
    element: HTMLElement,
    actions: RecoveryAction[],
    errorType: ErrorType,
  ): void {
    const panel = document.createElement("div");
    panel.className = "terra-recovery-panel";

    const title = document.createElement("h3");
    title.textContent = "Recovery Options";
    title.style.cssText =
      "margin: 0 0 8px 0; font-size: 0.875rem; font-weight: 600;";

    const description = document.createElement("p");
    description.textContent = this.getErrorDescription(errorType);
    description.style.cssText =
      "margin: 0; font-size: 0.75rem; color: hsl(var(--muted-foreground));";

    const actionsContainer = document.createElement("div");
    actionsContainer.className = "terra-recovery-actions";

    actions.forEach((action) => {
      const button = document.createElement("button");
      button.className = `terra-recovery-action terra-recovery-action-${action.priority}`;
      if (action.destructive) {
        button.classList.add("terra-recovery-action-destructive");
      }

      button.innerHTML = `${action.icon} ${action.label}`;
      button.onclick = () => {
        action.action();
        this.hideRecoveryPanel(element);
      };

      actionsContainer.appendChild(button);
    });

    panel.appendChild(title);
    panel.appendChild(description);
    panel.appendChild(actionsContainer);

    element.appendChild(panel);
    this.activePanels.set(element, panel);
  }

  hideRecoveryPanel(element: HTMLElement): void {
    const panel = this.activePanels.get(element);
    if (panel) {
      panel.remove();
      this.activePanels.delete(element);
    }
  }

  private getErrorDescription(errorType: ErrorType): string {
    switch (errorType) {
      case ErrorType.INVALID_DROP:
        return "The component cannot be placed in this location.";
      case ErrorType.CONSTRAINT_VIOLATION:
        return "This placement violates layout or design constraints.";
      case ErrorType.NETWORK_ERROR:
        return "Network connectivity issues detected.";
      case ErrorType.PERMISSION_DENIED:
        return "Insufficient permissions for this operation.";
      case ErrorType.COLLISION_DETECTED:
        return "Component placement conflicts with existing elements.";
      case ErrorType.SYSTEM_ERROR:
        return "An unexpected system error occurred.";
      default:
        return "An error occurred during the operation.";
    }
  }

  dispose(): void {
    this.activePanels.forEach((panel, element) => {
      panel.remove();
    });
    this.activePanels.clear();
  }
}

class ErrorLogger {
  private errorLog: Array<{
    timestamp: number;
    errorType: ErrorType;
    context: VisualFeedbackContext;
    message?: string;
    additionalData?: any;
  }> = [];

  logError(
    errorType: ErrorType,
    context: VisualFeedbackContext,
    message?: string,
    additionalData?: any,
  ): void {
    this.errorLog.push({
      timestamp: Date.now(),
      errorType,
      context,
      message,
      additionalData,
    });

    // Keep only last 100 errors
    if (this.errorLog.length > 100) {
      this.errorLog = this.errorLog.slice(-100);
    }

    console.warn(`Terra Error [${errorType}]:`, message, {
      context,
      additionalData,
    });
  }

  getStatistics() {
    const stats = {
      totalErrors: this.errorLog.length,
      errorsByType: {} as Record<ErrorType, number>,
      recentErrors: this.errorLog.slice(-10),
    };

    this.errorLog.forEach((error) => {
      stats.errorsByType[error.errorType] =
        (stats.errorsByType[error.errorType] || 0) + 1;
    });

    return stats;
  }
}

interface Position {
  x: number;
  y: number;
}
